import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import history from 'browserHistory'
import { useDispatch, useSelector } from 'react-redux'

import RoutesHelper from 'helpers/RoutesHelper'

import { RowLayoutHoverZone } from '../RowLayout'
import { EnhancedNoteControls } from './Controls'
import NoteIconWithPicker from '../NoteIconWithPicker'

import { useNoteDragAndDrop } from './useNoteDragAndDrop'

import useOnLongDragOver from '../../hooks/useOnLongDragOver'

import { getNote } from 'data/notes/selectors'
import { getNoteActivityStatus } from 'data/activityRegistry/selectors'
import { ACTIVE } from 'data/activityRegistry/record'
import { TooltipOnHover } from 'components/Utils/TooltipOnHover'
import { trackNoteClicked } from 'data/analytics/actions'
import {
  ExpandableRowLayout,
  ExpandableRowLayoutProps,
} from '../ExpandableRowLayout'

export type NoteItemProps = {
  id: string
  depth: number
  isActive: boolean
  isExpandable: boolean
  onToggleExpand: ExpandableRowLayoutProps['onToggleExpand']
  onDropNote: () => void
  isExpanded: boolean
  setLastClickedNoteId: (noteId: string) => void
  channelId: string
  title: string
  iconShape: string
  iconColor: string
  filterId?: string
  dndData?: any
  onlyAllowDropInside?: boolean
}

export const NoteItem = ({
  id,
  depth,
  isActive,
  isExpandable,
  onToggleExpand,
  isExpanded,
  setLastClickedNoteId,
  channelId,
  title = 'Untitled',
  iconShape,
  iconColor,
  filterId,
  dndData,
  onlyAllowDropInside,
}: NoteItemProps) => {
  const layoutRef = useRef<HTMLElement>(null)
  const [isMorePopoverOpen, setMorePopoverIsOpen] = React.useState(false)
  const { isDragOver, isDragging, hoverZone } = useNoteDragAndDrop(
    dndData.noteId,
    channelId,
    dndData.ancestorNoteIds,
    title,
    layoutRef,
    onlyAllowDropInside
  )

  const dispatch = useDispatch()
  const hasActivity = useSelector(
    state => getNoteActivityStatus(state, getNote(state, id)) === ACTIVE
  )

  useOnLongDragOver({
    isDragOver,
    enable: !isExpanded && isExpandable,
    onLongDragOver() {
      onToggleExpand(id)
    },
  })

  React.useEffect(
    () => {
      const isFocused = document.activeElement === layoutRef.current
      if (layoutRef.current && isActive && !isFocused) {
        layoutRef.current.focus()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layoutRef.current, isActive]
  )

  const handleClick = () => {
    dispatch(
      trackNoteClicked({
        source: `structure${filterId ? `-${filterId}` : ''}`,
      })
    )
    setLastClickedNoteId(id)
  }

  const handleNoteCreated = (createdNote: any) => {
    if (!isExpanded) {
      onToggleExpand(id)
    }

    setLastClickedNoteId(createdNote.id)
    const nextUrl = filterId
      ? RoutesHelper.noteFilterShow({
          filterId,
          noteId: createdNote.id,
        })
      : RoutesHelper.noteChannelShow({
          channelId,
          noteId: createdNote.id,
        })

    history.push(nextUrl)
  }

  return (
    <ExpandableRowLayout
      id={id}
      ref={layoutRef}
      onToggleExpand={onToggleExpand}
      isExpanded={isExpanded}
      onClick={handleClick}
      disableExpanding={!isExpandable}
      renderComponent={props => (
        <TooltipOnHover
          id={`tooltip-sidebar-${id}`}
          tooltipContent={title}
          placement="right"
          delayShow={1000}
        >
          <Link
            {...props}
            to={
              filterId
                ? RoutesHelper.noteFilterShow({ filterId, noteId: id })
                : RoutesHelper.noteChannelShow({ channelId, noteId: id })
            }
          />
        </TooltipOnHover>
      )}
      className="child-effects-on-hover"
      depth={depth}
      label={title || 'Untitled'}
      hidelabelOnHotjar
      isActive={isActive}
      isDragOver={isDragOver}
      isDragging={isDragging}
      hasActivity={hasActivity}
      hoverZone={hoverZone as RowLayoutHoverZone}
      icon={
        <NoteIconWithPicker
          noteId={id}
          iconShape={iconShape}
          iconColor={iconColor}
        />
      }
      rightPart={
        <EnhancedNoteControls
          className={
            isMorePopoverOpen ? '' : 'display-none flexbox-child-effect'
          }
          noteId={id}
          depth={depth}
          onMorePopoverIsOpenChange={setMorePopoverIsOpen}
          onNoteCreated={handleNoteCreated}
        />
      }
    />
  )
}
